export type RuntimeConfig = {
  turnstile?: {
    enabled: boolean;
    managedKiteKey: string | null;
  };
  sentry?: {
    dsn: string;
    environment?: string;
  };
  showNeedHelp?: boolean;
  iceLink?: string;
  loginLogoUrl?: string;
};

// --- Host integration typings ----------------------------------------------
declare global {
  interface Window {
    /** Host can set an object or a function returning (possibly promised) config */
    VP_RUNTIME_CONFIG?:
      | RuntimeConfig
      | (() => RuntimeConfig | Promise<RuntimeConfig>);
  }
}

let cfgPromise: Promise<RuntimeConfig> | null = null;
let config: RuntimeConfig | null = null;

// Allow programmatic override (e.g., tests or host late-binding)
export function setRuntimeConfig(cfg: RuntimeConfig) {
  config = cfg;
  cfgPromise = Promise.resolve(cfg);
}

// Optional: if host injects later, they can dispatch this event with detail: RuntimeConfig
//   window.dispatchEvent(new CustomEvent('vp:runtime-config', { detail: {...} }))
// window?.addEventListener?.('vp:runtime-config', (e: Event) => {
//   const detail = (e as CustomEvent).detail as RuntimeConfig | undefined;
//   if (detail) {
//     setRuntimeConfig(detail);
//   }
// });

function readJsonConfigNode(): Partial<RuntimeConfig> | null {
  const node =
    document.getElementById('vp-runtime-config') ||
    document.querySelector('script[type="application/json"][data-vp-config]');

  if (!node) {
    return null;
  }

  const raw = node.textContent ?? '';
  const text = raw.replace(/^\uFEFF/, ''); // strip BOM if present
  if (!text.trim()) {
    return null;
  }

  try {
    return JSON.parse(text) as Partial<RuntimeConfig>;
  } catch (e) {
    console.warn('[VP] Failed to parse #vp-runtime-config JSON:', e);
    return null;
  }
}

const readFromHost = async (): Promise<RuntimeConfig | null> => {
  if (typeof window === 'undefined') {
    return null;
  }

  const provided = window.VP_RUNTIME_CONFIG;
  if (!provided) {
    return null;
  }

  try {
    if (typeof provided === 'function') {
      const value = await provided();
      return value ?? null;
    }
    return provided;
  } catch {
    // Ignore host errors, fall back to fetch
    return null;
  }
};

const fetchFromEndpoint = async (): Promise<RuntimeConfig> => {
  const r = await fetch('/client-config', { credentials: 'same-origin' });
  if (!r.ok) {
    throw new Error(`Failed to fetch runtime config: ${r.status} ${r.statusText}`);
  }
  return (await r.json()) as RuntimeConfig;
};

export const getRuntimeConfig = async (): Promise<RuntimeConfig> => {
  if (config) {
    return config;
  }

  if (!cfgPromise) {
    cfgPromise = (async (): Promise<RuntimeConfig> => {
      const jsonCfg = readJsonConfigNode();
      if (jsonCfg) {
        config = jsonCfg;
        return jsonCfg as RuntimeConfig;
      }

      // 1) Prefer host-provided global/function
      const hostCfg = await readFromHost();
      if (hostCfg) {
        config = hostCfg;
        return hostCfg;
      }

      // 2) Fall back to endpoint
      const fetched = await fetchFromEndpoint();
      config = fetched;
      return fetched;
    })();
  }

  return cfgPromise;
};
